import React, { useState } from 'react';
import PropTypes from 'prop-types';
import momentPropTypes from 'react-moment-proptypes';
import { DateRangePicker, SingleDatePicker } from 'react-dates';
import styled from 'styled-components';
import moment from 'moment';

// Styles
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import './datePicker.scss';

const ErrorWrapper = styled.span`
    color: #f00;
    display: block;
    font-size: 12px;
    margin: 8px 0;
`;

const DatePicker = ({
    input,
    meta,
    singleDatePicker = false,
    startDate,
    endDate,
    startDateId,
    endDateId,
    numberOfMonths = 1,
    onSetStartDate,
    onSetEndDate,
    maximumNights,
    showDefaultInputIcon = true,
    ...props
}) => {
    const [focused, setFocused] = useState(false);
    const [focusedInput, setFocusedInput] = useState(null);

    // Ensure startDate and endDate are moment objects
    const validStartDate = startDate ? moment(startDate) : null;
    const validEndDate = endDate ? moment(endDate) : null;

    return (
        <>
            {singleDatePicker ? (
                <SingleDatePicker
                    {...props}
                    date={input?.value ? moment(input.value) : validStartDate}
                    onDateChange={(date) => {
                        if (input?.onChange) input.onChange(date);
                        if (onSetStartDate) onSetStartDate(date);
                    }}
                    focused={focused}
                    onFocusChange={({ focused }) => setFocused(focused)}
                    id={startDateId}
                    numberOfMonths={numberOfMonths}
                    showDefaultInputIcon={showDefaultInputIcon}
                />
            ) : (
                <DateRangePicker
                    minimumNights={0}
                    {...props}
                    startDate={validStartDate}
                    endDate={validEndDate}
                    onDatesChange={({ startDate, endDate }) => {
                        if (input?.onChange) {
                            input.onChange({
                                startDate: startDate ? startDate.format() : null,
                                endDate: endDate ? endDate.format() : null,
                            });
                        }
                        if (onSetStartDate) onSetStartDate(startDate);
                        if (onSetEndDate) onSetEndDate(endDate);
                    }}
                    focusedInput={focusedInput}
                    onFocusChange={(focusedInput) => setFocusedInput(focusedInput)}
                    startDateId={startDateId}
                    endDateId={endDateId}
                    numberOfMonths={numberOfMonths}
                    isOutsideRange={(day) => {
                        if (maximumNights && focusedInput === 'endDate' && validStartDate) {
                            return (
                                day.isBefore(validStartDate) ||
                                day.isAfter(validStartDate.clone().add(maximumNights, 'days')) ||
                                day.isAfter(moment().endOf('day'))
                            );
                        }
                        return moment().diff(day, 'days') < 0;
                    }}
                    showDefaultInputIcon={showDefaultInputIcon}
                />
            )}
            {meta?.touched && meta?.error && (
                <ErrorWrapper className="error-message">{meta.error}</ErrorWrapper>
            )}
        </>
    );
};

DatePicker.propTypes = {
    input: PropTypes.object,
    meta: PropTypes.object,
    singleDatePicker: PropTypes.bool,
    startDate: momentPropTypes.momentObj,
    endDate: momentPropTypes.momentObj,
    startDateId: PropTypes.string.isRequired,
    endDateId: PropTypes.string,
    numberOfMonths: PropTypes.number,
    onSetStartDate: PropTypes.func,
    onSetEndDate: PropTypes.func,
    maximumNights: PropTypes.number,
    showDefaultInputIcon: PropTypes.bool,
};

export default DatePicker;
